// ----------------------------------------------------------------------
// <copyright file="Izenda.SchedulingSearchStore.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { observable } from 'mobx';
import { map } from 'lodash/fp';
import uiState from 'logic/Izenda.UiState';
import SchedulingSearchCriteria from './models/Izenda.SchedulingSearchCriteria';
import SchedulingSearchResult from './models/Izenda.SchedulingSearchResult';
import PagingModel from 'share/components/controls/DataGrid/helpers/Izenda.PagingModel';
import systemFetcher from 'fetchers/Izenda.SystemSettingFetcher';
import dashboardFetcher from 'fetchers/Izenda.DashboardDataFetcher';
import reportFetcher from 'fetchers/Izenda.ReportsDataFetcher';
import CONSTANTS from 'logic/data/Izenda.SchedulingSearchConstants';

const PAGE_SEARCH_SIZE = CONSTANTS.PAGE_SEARCH_SIZE;
const SEARCH_KEY = CONSTANTS.SEARCH_KEY;
const DEFAULT_SORT_ORDER = CONSTANTS.DEFAULT_SORT_ORDER;
const REPORT_TYPE_VALUE = CONSTANTS.REPORT_TYPE_VALUE;
const TEMPLATE_TYPE = CONSTANTS.TEMPLATE_TYPE;

export default class SchedulingSearchStore {
  @observable criteria = new SchedulingSearchCriteria();
  @observable pageInfo = new PagingModel(DEFAULT_SORT_ORDER);
  @observable searchResult = new SchedulingSearchResult();
  @observable emailTemplates = null;

  /**
   * Load Email Template
   * @returns {*}
   */
  loadEmailTemplates() {
    return dashboardFetcher
      .fetchEmailTemplates(TEMPLATE_TYPE)
      .then(result => {
        this.emailTemplates = result;
        return result;
      });
  }

  /**
   * Load Schedules
   * @return {Promise}
   */
  loadSchedules() {
    const criteria = this.buildCriteriaData();
    const params = {
      systemLevel: !uiState.tenantId,
      tenantId: uiState.tenantId,
      pageIndex: 1,
      pageSize: PAGE_SEARCH_SIZE,
      sortOrders: this.pageInfo.buildSortOrder(),
      criteria: criteria
    };
    return systemFetcher
      .loadSchedules(params)
      .then(result => {
        this.searchResult.populateData(result);
        return result;
      });
  }

  /**
   * Update schedules on one grid
   * @param data
   */
  updateGridSchedules(data) {
    const criteria = this.buildCriteriaData();
    const pageInfo = data.pageInfo;
    const params = {
      systemLevel: false,
      tenantId: !!data.tenantId ? data.tenantId : null,
      pageIndex: pageInfo.pageIndex,
      pageSize: pageInfo.pageSize,
      sortOrders: pageInfo.buildSortOrder(),
      criteria: criteria
    };
    return systemFetcher
      .loadSchedules(params)
      .then(result => {
        this.searchResult.populateData(result, data.pageInfo);
        return result;
      });
  }

  /**
   * Load report dashbord name
   * @param tenantId
   * @returns {*}
   */
  loadReportDashboardName(tenantId) {
    const reportingType = REPORT_TYPE_VALUE[reportingType];
    tenantId = tenantId || uiState.tenantId;
    return systemFetcher
      .getReportDashboardNameByTenant(reportingType || 0, tenantId)
      .then(result =>
        this.criteria.setValueByKey(
          'reportDashboardNameOptions',
          map(item => ({value: item, label: item}), result)
        )
      );
  }

  /**
   * Load creator by tenant
   * @param tenantId
   * @returns {*}
   */
  loadCreatorByTenant(tenantId) {
    tenantId = tenantId || uiState.tenantId;
    return systemFetcher
      .getCreatedByTenant(tenantId)
      .then(result => this.criteria.setOptionByKey('createdByOptions', result));
  }

  /**
   * Load recipients by tenant
   * @param tenantId
   * @returns {*}
   */
  loadRecipientsByTenant(tenantId) {
    tenantId = tenantId || uiState.tenantId;
    return systemFetcher
      .getRecipientByTenant(tenantId)
      .then(result => this.criteria.setOptionByKey('recipientsOptions', result));
  }

  /**
   * Build search criteria array
   * @returns {Array}
   */
  buildCriteriaData() {
    const criteria = this.criteria.buildDataForSearch();
    const criteriaKeys = Object.keys(SEARCH_KEY);
    return map(key => ({
      key: SEARCH_KEY[key],
      value: criteria[key]
    }), criteriaKeys);
  }

  /**
   * Save Schedules
   * @param {object} subscription
   * @returns {promise}
   */
  saveSchedule(subscription) {
    let postData;
    let saveFunction;
    if (subscription.dashboardId) {
      postData = {
        id: subscription.dashboardId,
        subscriptions: [subscription]
      };
      saveFunction = dashboardFetcher.saveSubscriptions;
    } else {
      postData = [subscription];
      saveFunction = reportFetcher.updateSubscriptions;
    }
    return saveFunction(postData);
  }

  /**
   * Validate Schedule
   * @param {object} schedule
   * @param {object} commonFilterFields
   * @returns {*|Promise}
   */
  validateSchedule(schedule, commonFilterFields) {
    const fetcher = schedule.dashboardId ? dashboardFetcher : reportFetcher;
    return fetcher.validateSubscription(schedule.omitData(), commonFilterFields);
  }

  /**
   * Delete one or many schedules
   * @param {array} data
   */
  deleteSchedules(data) {
    return systemFetcher.deleteSchedules(data);
  }

  /**
   * Reset Search result data
   */
  resetData() {
    this.searchResult = new SchedulingSearchResult();
    this.pageInfo = new PagingModel(DEFAULT_SORT_ORDER);
    this.criteria.resetData();
  }
}



// WEBPACK FOOTER //
// ./ui/logic/Izenda.SchedulingSearchStore.js